<template>
  <div class="salary-settings">
    <BaseCard :title="texts.salarySettings">
      <div class="salary-settings__group">
        <div class="salary-settings__group-title">
          {{ texts.officialsGroup }}
        </div>
        <div class="salary-settings__fields">
          <BaseInput
            type="select"
            :label="texts.reportingPeriod"
            v-model="form.officials.reportingPeriod"
            :items="reportingPeriodOptions"
            item-text="text"
            item-value="value"
            :placeholder="texts.reportingPeriodPlaceholder"
            hide-details
            :disabled="loadingSalaryPeriodDay"
          />
          <BaseInput
            type="date-picker"
            :label="texts.startingDate"
            v-model="form.officials.startingDate"
            :placeholder="texts.startingDateDatePlaceholder"
            hide-details="auto"
            :disabled="loadingSalaryPeriodDay"
          />
          <BaseInput
            type="autocomplete"
            :label="texts.rolesLabel"
            v-model="form.officials.roles"
            :items="officialsRoleOptions"
            item-text="role_name"
            item-value="id"
            :placeholder="texts.rolesPlaceholder"
            hide-details="auto"
            :disabled="loadingRoles || loadingSalaryPeriodDay"
            multiple
            chips
            deletable-chips
          />
        </div>
      </div>
      <div class="salary-settings__group">
        <div class="salary-settings__group-title">
          {{ texts.workersGroup }}
        </div>
        <div class="salary-settings__fields">
          <BaseInput
            type="select"
            :label="texts.reportingPeriod"
            v-model="form.workers.reportingPeriod"
            :items="reportingPeriodOptions"
            item-text="text"
            item-value="value"
            :placeholder="texts.reportingPeriodPlaceholder"
            hide-details
            :disabled="loadingSalaryPeriodDay"
          />
          <BaseInput
            type="date-picker"
            :label="texts.startingDate"
            v-model="form.workers.startingDate"
            :placeholder="texts.startingDateDatePlaceholder"
            hide-details="auto"
            :disabled="loadingSalaryPeriodDay"
          />
          <BaseInput
            type="autocomplete"
            :label="texts.rolesLabel"
            v-model="form.workers.roles"
            :items="workersRoleOptions"
            item-text="role_name"
            item-value="id"
            :placeholder="texts.rolesPlaceholder"
            hide-details="auto"
            :disabled="loadingRoles || loadingSalaryPeriodDay"
            multiple
            chips
            deletable-chips
          />
        </div>
      </div>
    </BaseCard>
    <div class="fixedBtn">
      <BaseTooltip :tooltip="texts.regenerateSalaryReports">
        <BaseButton
          :disabled="loadingSalaryPeriodDay || savingSalaryPeriodDay || regeneratingReports"
          @click="onRegenerateSalaryReports"
          round
          icon_only
          size="60"
          default_icon="button-repeat"
        />
      </BaseTooltip>
      <BaseTooltip :tooltip="buttons.save">
        <BaseButton
          :disabled="loadingSalaryPeriodDay || savingSalaryPeriodDay || !formValid"
          @click="onSave"
          round
          icon_only
          size="60"
          default_icon="button-save"
        />
      </BaseTooltip>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'SettingCompanySalarySettings',
  components: {},
  data() {
    return {
      form: {
        officials: {
          reportingPeriod: '14_days',
          startingDate: null,
          roles: [],
        },
        workers: {
          reportingPeriod: '14_days',
          startingDate: null,
          roles: [],
        },
      },
      reportingPeriodOptions: [
        { text: '14 days', value: '14_days' },
        { text: '1 month', value: '30_days' },
      ],
      rolesOptions: [],
      loadingRoles: false,
      loadingSalaryPeriodDay: false,
      savingSalaryPeriodDay: false,
      regeneratingReports: false,
      initialLoadComplete: false,
    }
  },
  computed: {
    ...mapGetters(['apiDomain']),
    texts() {
      const translations =
        this.localStorage?.translations?.pages?.['setting-setting_company']?.all || {}

      return {
        salarySettings: translations['salary_settings'] || 'Salary settings',
        reportingPeriod: translations['reporting_period'] || 'Reporting period',
        reportingPeriodPlaceholder: translations['reporting_period_placeholder'] || 'Select period',
        startingDate: translations['starting_day'] || 'Starting day',
        startingDatePlaceholder: translations['starting_date_placeholder'] || 'Day of month',
        startingDateDatePlaceholder: translations['starting_date_date_placeholder'] || 'Select date',
        officialsGroup: translations['officials_group'] || 'Officials',
        workersGroup: translations['workers_group'] || 'Workers',
        rolesLabel: translations['roles_label'] || 'Roles',
        rolesPlaceholder: translations['roles_placeholder'] || 'Select roles',
        regenerateSalaryReports:
          translations['regenerate_salary_reports'] || 'Regenerate salary reports',
        regenerateSalaryReportsSuccess:
          translations['regenerate_salary_reports_success'] ||
          'Salary reports regeneration started',
      }
    },
    notifications() {
      return this.localStorage?.translations?.pages?.common?.notifications || {}
    },
    buttons() {
      return this.localStorage?.translations?.pages?.common?.buttons || {}
    },
    startingDateValid() {
      return (
        this.isValidYmdDate(this.form.officials.startingDate) &&
        this.isValidYmdDate(this.form.workers.startingDate)
      )
    },
    reportingPeriodValid() {
      return (
        this.isValidPeriodDays(this.form.officials.reportingPeriod) &&
        this.isValidPeriodDays(this.form.workers.reportingPeriod)
      )
    },
    formValid() {
      return this.startingDateValid && this.reportingPeriodValid
    },
    officialsRoleOptions() {
      return this.filterRoleOptions(this.form.officials.roles, this.form.workers.roles)
    },
    workersRoleOptions() {
      return this.filterRoleOptions(this.form.workers.roles, this.form.officials.roles)
    },
  },
  created() {
    this.fetchSalaryPeriodDay()
    this.fetchRoles()
  },
  watch: {
    'form.officials.roles'(next) {
      this.syncRolesBetweenGroups('workers', next)
    },
    'form.workers.roles'(next) {
      this.syncRolesBetweenGroups('officials', next)
    },
  },
  methods: {
    onSave() {
      if (!this.formValid) return
      this.saveSalaryPeriodDay()
    },
    onRegenerateSalaryReports() {
      this.regenerateSalaryReports()
    },
    async fetchSalaryPeriodDay() {
      this.loadingSalaryPeriodDay = true
      try {
        const response = await axios.get(
          this.apiDomain + '/api/common/company/salary-period-day',
        )
        const salaryPeriodDay =
          response?.data?.data?.salary_period_day ?? response?.data?.salary_period_day ?? null
        this.applySalaryPeriodDay(salaryPeriodDay)
      } catch (error) {
        this.$store.dispatch(
          'popup/onMessage',
          { message: this.notifications['oshibka'] || 'Error', IsError: false },
          { root: true },
        )
      } finally {
        this.loadingSalaryPeriodDay = false
        this.initialLoadComplete = true
      }
    },
    async saveSalaryPeriodDay() {
      if (this.savingSalaryPeriodDay) return
      const salaryPeriodDayValue = this.buildSalaryPeriodDayPayload()
      if (!salaryPeriodDayValue) return
      this.savingSalaryPeriodDay = true
      try {
        const response = await axios.post(
          this.apiDomain + '/api/admin/common/company/salary-period-day',
          {
            salary_period_day: salaryPeriodDayValue,
          },
        )
        const salaryPeriodDay =
          response?.data?.data?.salary_period_day ?? response?.data?.salary_period_day
        if (salaryPeriodDay !== undefined) {
          this.applySalaryPeriodDay(salaryPeriodDay)
        }
        this.$store.dispatch(
          'popup/onMessage',
          {
            message:
              this.notifications['izmeneniya-uspeshno-sohraneny'] || 'Changes saved',
            IsError: false,
          },
          { root: true },
        )
      } catch (error) {
        this.$store.dispatch(
          'popup/onMessage',
          { message: this.notifications['oshibka'] || 'Error', IsError: false },
          { root: true },
        )
      } finally {
        this.savingSalaryPeriodDay = false
      }
    },
    async regenerateSalaryReports() {
      if (this.regeneratingReports) return
      this.regeneratingReports = true
      try {
        await axios.post(
          this.apiDomain + '/api/admin/common/company/salary-periods/regenerate',
        )
        this.$store.dispatch(
          'popup/onMessage',
          { message: this.texts.regenerateSalaryReportsSuccess, IsError: false },
          { root: true },
        )
      } catch (error) {
        this.$store.dispatch(
          'popup/onMessage',
          { message: this.notifications['oshibka'] || 'Error', IsError: false },
          { root: true },
        )
      } finally {
        this.regeneratingReports = false
      }
    },
    normalizeDay(value) {
      const day = this.extractDayNumber(value)
      if (!day || day < 1 || day > 31) return null
      return day
    },
    normalizeRoles(value) {
      if (!Array.isArray(value)) return []
      return value
        .map((roleId) => Number(roleId))
        .filter((roleId) => Number.isFinite(roleId))
    },
    normalizePeriodDays(value) {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'number') return value
      if (typeof value === 'string') {
        const match = value.match(/^(\d+)_days$/)
        if (match) return Number(match[1])
        if (/^\d+$/.test(value)) return Number(value)
      }
      return null
    },
    isValidDay(value) {
      return !!this.normalizeDay(value)
    },
    normalizeDateYmd(value) {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)
        if (this.isValidCalendarDate(year, month, day)) {
          return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        }
      }
      if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
        const [day, month, year] = value.split('.')
        if (this.isValidCalendarDate(Number(year), Number(month), Number(day))) {
          return `${year}-${month}-${day}`
        }
      }
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
      }
      return null
    },
    isValidYmdDate(value) {
      return !!this.normalizeDateYmd(value)
    },
    isValidPeriodDays(value) {
      const normalized = this.normalizePeriodDays(value)
      return !!normalized && normalized > 0
    },
    async fetchRoles() {
      this.loadingRoles = true
      try {
        const response = await axios.get(
          this.apiDomain +
            '/api/admin/common/role/list?limit=0&order[]=role_name:asc',
        )
        const roles = response?.data?.data ?? response?.data?.roles ?? []
        this.rolesOptions = Array.isArray(roles) ? roles : []
      } catch (error) {
        this.$store.dispatch(
          'popup/onMessage',
          { message: this.notifications['oshibka'] || 'Error', IsError: false },
          { root: true },
        )
      } finally {
        this.loadingRoles = false
      }
    },
    filterRoleOptions(currentRoles, otherRoles) {
      const current = new Set(this.normalizeRoles(currentRoles))
      const other = new Set(this.normalizeRoles(otherRoles))
      return this.rolesOptions.filter(
        (role) => !other.has(role.id) || current.has(role.id),
      )
    },
    syncRolesBetweenGroups(targetGroupKey, selectedRoles) {
      const selected = new Set(this.normalizeRoles(selectedRoles))
      const targetRoles = this.normalizeRoles(this.form[targetGroupKey].roles)
      const filtered = targetRoles.filter((roleId) => !selected.has(roleId))
      if (filtered.length !== targetRoles.length) {
        this.form[targetGroupKey].roles = filtered
      }
    },
    applySalaryPeriodDay(value) {
      if (!value) return
      if (typeof value === 'number') {
        this.form.officials.startingDate = this.dayToCurrentMonthDate(value)
        this.form.workers.startingDate = this.dayToCurrentMonthDate(value)
        return
      }
      if (value.day !== undefined || value.period_days !== undefined) {
        this.applyGroupSalaryPeriodDay('officials', value)
        this.applyGroupSalaryPeriodDay('workers', value)
        return
      }
      if (value.officials || value.workers) {
        this.applyGroupSalaryPeriodDay('officials', value.officials)
        this.applyGroupSalaryPeriodDay('workers', value.workers)
      }
    },
    applyGroupSalaryPeriodDay(groupKey, groupValue) {
      if (!groupValue) return
      const date =
        this.normalizeDateYmd(groupValue.day) || this.dayToCurrentMonthDate(groupValue.day)
      const day = this.normalizeDay(groupValue.day)
      const periodDays = this.normalizePeriodDays(groupValue.period_days)
      const roles = this.normalizeRoles(groupValue.roles)
      if (date !== null) {
        this.form[groupKey].startingDate = date
      } else if (day !== null) {
        this.form[groupKey].startingDate = day
      }
      if (periodDays !== null) {
        this.form[groupKey].reportingPeriod = `${periodDays}_days`
      }
      if (Array.isArray(groupValue.roles)) {
        this.form[groupKey].roles = roles
      }
    },
    buildSalaryPeriodDayPayload() {
      const officials = this.buildGroupPayload('officials', this.form.officials)
      const workers = this.buildGroupPayload('workers', this.form.workers)
      if (!officials || !workers) return null
      return {
        officials,
        workers,
      }
    },
    buildGroupPayload(groupKey, group) {
      const day = this.normalizeDateYmd(group.startingDate)
      const periodDays = this.normalizePeriodDays(group.reportingPeriod)
      if (!day || !periodDays) return null
      const roles = this.normalizeRoles(group.roles)
      return {
        day,
        period_days: periodDays,
        roles,
      }
    },
    extractDayNumber(value) {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'number') return value
      if (value instanceof Date) return value.getDate()
      if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
          const parts = value.split('-')
          return Number(parts[2]) || null
        }
        if (/^\d{1,2}$/.test(value)) return Number(value)
        const parsed = new Date(value)
        if (!Number.isNaN(parsed.getTime())) return parsed.getDate()
      }
      return null
    },
    isValidCalendarDate(year, month, day) {
      if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return false
      }
      if (year < 1000 || month < 1 || month > 12 || day < 1 || day > 31) return false
      const date = new Date(Date.UTC(year, month - 1, day))
      return (
        date.getUTCFullYear() === year &&
        date.getUTCMonth() + 1 === month &&
        date.getUTCDate() === day
      )
    },
    dayToCurrentMonthDate(value) {
      const day = this.normalizeDay(value)
      if (!day) return null
      const now = new Date()
      const year = now.getFullYear()
      const month = now.getMonth() + 1
      const daysInMonth = new Date(year, month, 0).getDate()
      const safeDay = Math.min(day, daysInMonth)
      return `${year}-${String(month).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`
    },
  },
}
</script>

<style scoped lang="scss">
.salary-settings {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.salary-settings__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.salary-settings__group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.salary-settings__group + .salary-settings__group {
  margin-top: 16px;
}

.salary-settings__group-title {
  font-weight: 600;
  font-size: 14px;
}

.fixedBtn {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
